import {useEffect, useRef} from 'react';
import areEqual from 'react-fast-compare';
import {useReactFlow} from 'reactflow';

import {FlowchartRenderData} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  ProcessFlowchartStaticNode,
  ProcessFlowchartStaticEdge,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {DEFAULT_FIT_VIEW_OPTIONS} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import usePrevious from 'ui-library/hooks/usePrevious';
import omitKey from 'util/omitKey';

// no need to fitView when the status counts change.
// if you are zoomed into a flowchart and a liveupdate comes in, we shouldn't refit
// the zoom unless a new node is added (probably not even then, but that's harder to solve).
function stripStatusCounts(
  renderData: FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge>
): FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge> {
  return {
    nodes: renderData.nodes.map((node) => ({
      ...node,
      data: {
        ...omitKey(node.data, 'statusCounts'),
      },
    })),
    edges: renderData.edges,
    centersBoundingBox: renderData.centersBoundingBox,
  };
}

export default function useAutoFitViewOnFlowchartChange(
  renderData: FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge>
): void {
  const previousRenderData = usePrevious(renderData);

  const {fitView} = useReactFlow();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // In general, we only enqueue fitView if isDifferentData is true (i.e. the flowchart has changed).
  // But if an autofit has been queued and not yet run, we want to requeue it.
  // Without this, we have the following problem:
  // time 0:
  //    - Width and height of the flowchart are not yet set
  //        - both getNodes and getEdges will return empty arrays
  //    - enqueue fitView via setTimeout
  // time 1:
  //    - width and height are known
  //    - Call fitView << if we are simpling checking isDifferentData, we won't enqueue fitView
  // fitToView runs: nothing happens.
  const hasAutoFitOnce = useRef<boolean>(false);

  useEffect(() => {
    const isDifferentData =
      previousRenderData != null &&
      !areEqual(stripStatusCounts(renderData), stripStatusCounts(previousRenderData));

    if (!hasAutoFitOnce.current || isDifferentData) {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
      if (isDifferentData) {
        hasAutoFitOnce.current = false;
      }
      // We have to defer the call to `fitView` until the next render cycle, after the newly
      // formatted flowchart has been prepared.
      timeoutRef.current = setTimeout(() => {
        hasAutoFitOnce.current = true;
        fitView(DEFAULT_FIT_VIEW_OPTIONS);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderData]);

  useEffect(
    () => () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );
}
